<template>
  <div class="events-map-container">
    <div v-if="!isMapLoaded" class="map-loading">
      <div class="loading-spinner">🗺️</div>
      <p>Загрузка карты...</p>
    </div>

    <div v-if="mapError" class="map-error">
      <div class="error-icon">⚠️</div>
      <p>Ошибка загрузки карты</p>
      <p class="error-details">{{ mapError }}</p>
    </div>

    <div
      ref="mapContainer"
      class="events-map"
      :class="{ 'map-hidden': !isMapLoaded || mapError }"
      :style="{ height: mapHeight + 'px' }"
    ></div>

    <!-- Custom popup for event details -->
    <div v-if="selectedEvent" class="custom-popup" @click="closePopup">
      <div class="popup-content" @click.stop>
        <div class="popup-header">
          <h4>{{ selectedEvent.attributes.name || 'Мероприятие' }}</h4>
          <button class="popup-close" @click="closePopup">&times;</button>
        </div>
        <div class="popup-body">
          <p><strong>Дата:</strong> {{ formatDate(selectedEvent.attributes.starts_at) }} - {{ formatDate(selectedEvent.attributes.ends_at) }}</p>
          <p><strong>Город:</strong> {{ selectedEvent.attributes.city || 'Не указан' }}</p>
          <p><strong>Адрес:</strong> {{ selectedEvent.attributes.address || 'Не указан' }}</p>
          <p><strong>Участников:</strong> {{ selectedEvent.attributes.max_players || 'Не указано' }}</p>
          <p><strong>Статус:</strong> {{ getStatusText(selectedEvent.attributes.status) }}</p>
          <a :href="eventShowPath(selectedEvent)" class="event-link">Подробнее</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EventsMap',
  props: {
    events: {
      type: Array,
      default: () => []
    },
    mapHeight: {
      type: Number,
      default: 400
    }
  },
  data() {
    return {
      isMapLoaded: false,
      mapError: null,
      map: null,
      markers: [],
      mapCenter: [55.76, 37.64], // Moscow center as default
      mapZoom: 4,
      initRetryCount: 0,
      maxRetries: 3,
      containerReady: false,
      selectedEvent: null
    }
  },
  computed: {
    eventsWithCoordinates() {
      return this.events.filter(event =>
        event.attributes.latitude &&
        event.attributes.longitude &&
        parseFloat(event.attributes.latitude) !== 0 &&
        parseFloat(event.attributes.longitude) !== 0
      )
    }
  },
  watch: {
    eventsWithCoordinates: {
      handler: 'updateMapMarkers',
      deep: true
    }
  },
  mounted() {
    // Wait for the next tick to ensure DOM is ready
    this.$nextTick(() => {
      // Check if container ref is available
      if (this.$refs.mapContainer) {
        this.containerReady = true;
        this.initMap();
      } else {
        // Try again after a short delay
        setTimeout(() => {
          this.$nextTick(() => {
            if (this.$refs.mapContainer) {
              this.containerReady = true;
              this.initMap();
            } else {
              console.error('Container ref not available');
              this.mapError = 'Ошибка инициализации контейнера карты';
            }
          });
        }, 100);
      }
    });
  },
  beforeUnmount() {
    // Clean up markers first
    if (this.markers && this.markers.length > 0) {
      this.markers.forEach(marker => {
        try {
          if (marker && this.map && this.map.geoObjects) {
            this.map.geoObjects.remove(marker);
          }
        } catch (error) {
          console.warn('Error removing marker during cleanup:', error);
        }
      });
      this.markers = [];
    }

    // Clean up map
    if (this.map) {
      try {
        this.map.destroy();
      } catch (error) {
        console.warn('Error destroying map during cleanup:', error);
      }
      this.map = null;
    }
  },
  methods: {
    async initMap() {
      try {
        // Double-check container availability
        if (!this.$refs.mapContainer) {
          console.error('Container ref not available in initMap');
          throw new Error('Map container not found');
        }

        const container = this.$refs.mapContainer;

        // Check if Yandex Maps API is already loaded
        if (typeof ymaps === 'undefined') {
          await this.loadYandexMapsAPI();
        }

        // Initialize the map
        await ymaps.ready();

        // Show the container and set explicit dimensions BEFORE creating the map
        container.style.width = '100%';
        container.style.height = this.mapHeight + 'px';
        container.style.minHeight = '400px';
        container.style.display = 'block';

        // Remove the hidden class to ensure proper dimensions
        container.classList.remove('map-hidden');

        // Wait a bit for the container to get proper dimensions
        await new Promise(resolve => setTimeout(resolve, 50));

        this.map = new ymaps.Map(container, {
          center: this.mapCenter,
          zoom: this.mapZoom,
          controls: ['zoomControl', 'fullscreenControl']
        }, {
          zoomControlPosition: { right: 10, top: 10 },
          fullscreenControlPosition: { right: 10, top: 50 }
        });

        // Add map click handler as fallback
        this.map.events.add('click', (e) => {
          this.handleMapClick(e.get('coords'));
        });

        this.isMapLoaded = true;

        // Update map view and markers after a longer delay to ensure map is fully initialized
        setTimeout(() => {
          this.updateMapView();
          this.updateMapMarkers();
        }, 500);

      } catch (error) {
        console.error('Error initializing Yandex Map:', error);

        // Retry initialization if we haven't exceeded max retries
        if (this.initRetryCount < this.maxRetries) {
          this.initRetryCount++;
          console.log(`Retrying map initialization (attempt ${this.initRetryCount}/${this.maxRetries})`);

          // Wait longer before retrying
          setTimeout(() => {
            this.initMap();
          }, 500 * this.initRetryCount);
          return;
        }

        this.mapError = 'Не удалось загрузить карту. Проверьте подключение к интернету.';
        this.isMapLoaded = false;
      }
    },

    loadYandexMapsAPI() {
      return new Promise((resolve, reject) => {
        // Check if script is already loaded
        if (document.querySelector('script[src*="api-maps.yandex.ru"]')) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        const apiKey = import.meta.env.VITE_YANDEX_MAPS_API_KEY || 'your_api_key_here';
        script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU`;
        script.async = true;

        script.onload = () => {
          console.log('Yandex Maps API loaded successfully');
          resolve();
        };

        script.onerror = () => {
          console.error('Failed to load Yandex Maps API');
          reject(new Error('Failed to load Yandex Maps API'));
        };

        document.head.appendChild(script);
      });
    },

    updateMapView() {
      if (!this.map || this.eventsWithCoordinates.length === 0) {
        console.log('Map or events not available for view update');
        return;
      }

      try {
        // Calculate bounds to fit all events
        const coordinates = this.eventsWithCoordinates
          .map(event => [
            parseFloat(event.attributes.latitude),
            parseFloat(event.attributes.longitude)
          ])
          .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

        if (coordinates.length === 0) {
          console.log('No valid coordinates found for map view update');
          return;
        }

        // Calculate center point
        const avgLat = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
        const avgLng = coordinates.reduce((sum, coord) => sum + coord[1], 0) / coordinates.length;

        this.mapCenter = [avgLat, avgLng];

        // Adjust zoom based on number of events
        let zoom = 4;
        if (coordinates.length === 1) {
          zoom = 12;
        } else if (coordinates.length <= 3) {
          zoom = 8;
        } else {
          zoom = 6;
        }

        this.map.setCenter(this.mapCenter, zoom);
      } catch (error) {
        console.error('Error updating map view:', error);
      }
    },

    updateMapMarkers() {
      if (!this.map) {
        return;
      }

      if (!this.map.geoObjects) {
        return;
      }

      try {
        // Clear existing markers
        this.markers.forEach(marker => {
          try {
            this.map.geoObjects.remove(marker);
          } catch (error) {
            console.warn('Error removing marker:', error);
          }
        });
        this.markers = [];

        // Add markers one by one with delays to prevent API errors
        this.addMarkersSequentially(this.eventsWithCoordinates, 0);

      } catch (error) {
        console.error('Error updating map markers:', error);
      }
    },

    addMarkersSequentially(events, index) {
      if (index >= events.length) {
        return;
      }

      const event = events[index];

      try {
        const coords = [
          parseFloat(event.attributes.latitude),
          parseFloat(event.attributes.longitude)
        ];

        // Validate coordinates
        if (isNaN(coords[0]) || isNaN(coords[1])) {
          console.warn('Invalid coordinates for event:', event.id, coords);
          // Continue with next marker
          setTimeout(() => this.addMarkersSequentially(events, index + 1), 100);
          return;
        }

        // Additional coordinate validation
        if (coords[0] < -90 || coords[0] > 90 || coords[1] < -180 || coords[1] > 180) {
          console.warn('Coordinates out of valid range for event:', event.id, coords);
          // Continue with next marker
          setTimeout(() => this.addMarkersSequentially(events, index + 1), 100);
          return;
        }

        // Create simple marker without balloon content (we'll use custom popup)
        let marker;
        try {
          // Create marker with minimal options
          marker = new ymaps.Placemark([coords[0], coords[1]], {}, {
            preset: 'islands#redDotIcon'
          });
        } catch (error) {
          console.error('Error creating Placemark:', error);
          // Try alternative approach
          try {
            marker = new ymaps.Placemark(coords, {}, {
              preset: 'islands#redDotIcon'
            });
          } catch (altError) {
            console.error('Alternative approach also failed:', altError);
            return; // Skip this marker
          }
        }

        // Suppress the bounds calculation error by wrapping the add operation
        try {
          this.map.geoObjects.add(marker);
          this.markers.push(marker);

          // Add click event after marker is added to ensure it works
          setTimeout(() => {
            try {
              marker.events.add('click', () => {
                this.onMarkerClick(event);
              });

              // Also try adding a direct click handler to the marker element
              setTimeout(() => {
                try {
                  const markerElement = marker.getOverlaySync().getElementSync();
                  if (markerElement) {
                    markerElement.addEventListener('click', (e) => {
                      e.stopPropagation();
                      this.onMarkerClick(event);
                    });
                  }
                } catch (elementError) {
                  console.warn('Error adding direct click handler:', elementError);
                }
              }, 100);

            } catch (error) {
              console.warn('Error adding click event to marker:', error);
            }
          }, 50);

        } catch (error) {
          // Suppress the bounds calculation error but log it for debugging
          if (error.message && error.message.includes('Cannot read properties of undefined')) {
            // Still add the marker to our array even if there was an error
            this.markers.push(marker);
          } else {
            throw error; // Re-throw other errors
          }
        }

        // Add next marker after a delay
        setTimeout(() => {
          this.addMarkersSequentially(events, index + 1);
        }, 200);

      } catch (error) {
        console.error('Error creating marker for event:', event.id, error);
        // Continue with next marker even if this one fails
        setTimeout(() => this.addMarkersSequentially(events, index + 1), 200);
      }
    },

    onMarkerClick(event) {
      this.selectedEvent = event;
    },

    handleMapClick(coords) {
      // Check if click is near any marker
      this.eventsWithCoordinates.forEach(event => {
        const eventCoords = [
          parseFloat(event.attributes.latitude),
          parseFloat(event.attributes.longitude)
        ];

        // Calculate distance between click and marker (rough approximation)
        const latDiff = Math.abs(coords[0] - eventCoords[0]);
        const lngDiff = Math.abs(coords[1] - eventCoords[1]);

        // If click is within ~0.01 degrees of marker (roughly 1km), show popup
        if (latDiff < 0.01 && lngDiff < 0.01) {
          this.selectedEvent = event;
        }
      });
    },

    closePopup() {
      this.selectedEvent = null;
    },

    eventShowPath(event) {
      const locale = document.querySelector('meta[name="current-locale"]')?.getAttribute('content') || 'ru';
      return `/${locale}/events/${event.id}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'Не указана';
      const date = new Date(dateString);

      // Check if date is valid (not NaN or Invalid Date)
      if (isNaN(date.getTime())) return 'Не указана';

      return `${date.toLocaleDateString('ru-RU', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })} - ${date.toLocaleDateString('ru-RU', { hour: 'numeric', minute: 'numeric' })}`;
    },

    getStatusText(status) {
      const statusMap = {
        'draft': 'Черновик',
        'active': 'Активно',
        'expired': 'Завершено',
        'archived': 'Архив'
      };
      return statusMap[status] || status;
    }
  }
}
</script>

<style scoped>
.events-map-container {
  margin: 20px 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.events-map {
  width: 100%;
  min-height: 400px;
  background-color: #f5f5f5;
  position: relative;
}

.map-hidden {
  display: none !important;
}

.map-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  background-color: #f5f5f5;
  color: #bd9c65;
}

.loading-spinner {
  font-size: 32px;
  margin-bottom: 10px;
  animation: pulse 2s infinite;
}

.map-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  background-color: #f5f5f5;
  color: #dc3545;
  text-align: center;
  padding: 20px;
}

.error-icon {
  font-size: 32px;
  margin-bottom: 10px;
}

.error-details {
  font-size: 14px;
  margin-top: 10px;
  color: #6c757d;
  max-width: 300px;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Yandex Maps balloon styling */
:deep(.ymaps-2-1-79-balloon) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.ymaps-2-1-79-balloon__content) {
  padding: 15px;
  font-family: inherit;
}

.event-balloon {
  max-width: 280px;
}

.event-balloon h4 {
  margin: 0 0 10px 0;
  color: #60451f;
  font-size: 16px;
  font-weight: bold;
}

.event-info p {
  margin: 5px 0;
  font-size: 14px;
  line-height: 1.4;
  color: #60451f;
}

.event-link {
  display: inline-block;
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #c29c58;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  transition: background-color 0.2s ease;
}

.event-link:hover {
  background-color: #b08f4f;
  color: white;
  text-decoration: none;
}

/* Map controls styling */
:deep(.ymaps-2-1-79-controls__control) {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:deep(.ymaps-2-1-79-controls__control_toolbar) {
  background-color: white;
  border: 1px solid #bd9c65;
}

/* Custom popup styling */
.custom-popup {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.popup-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 20px;
  max-width: 350px;
  width: 90%;
  text-align: center;
  position: relative;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.popup-header h4 {
  margin: 0;
  color: #60451f;
  font-size: 20px;
  font-weight: bold;
}

.popup-close {
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.popup-close:hover {
  background-color: #e0e0e0;
}

.popup-body p {
  margin: 8px 0;
  font-size: 15px;
  line-height: 1.6;
  color: #60451f;
}

.popup-body p strong {
  color: #c29c58;
}
</style>
